import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import "./MuseumWalkthrough.css";

export type Work = {
  source?: "Tezos" | "Ethereum";
  chain?: "ethereum" | "base" | "zora" | "polygon";
  contract?: string;
  tokenId?: string;
  artist: string;
  title: string;
  year: string;
  medium?: string;
  collection?: string;
  media?: string;
  mediaType?: "image" | "video";
  animation?: string;
};

// A handful of works from the same series/collection shown together on one slide as a small
// mosaic instead of one-per-slide -- e.g. 4 pieces from the same generative collection read
// better as a set than as 4 separate slides. Counts as a single item within its room for
// position/count purposes ("N / total").
export type WorkGroup = {
  kind: "group";
  label: string;
  works: Work[];
  // Sian: hovering a token in the Community Access Tokens cluster should surface its own
  // title, since the three are distinct named pieces (Black Dave Token, BGCC Metaverse,
  // Black@ Community Token) rather than numbered variants of one collection -- "not needed
  // in the other PFPs" (NounPunks, Ohnahji, Loser Club), so this defaults off.
  showItemTitles?: boolean;
};

// A text-only slide for something that happened rather than something MoDDA holds -- e.g. Proof of
// Culture's "Dec 2020 - June 2021" stop, where five events from the wider record sit between
// the 2020 and 2021 sections. Rendered as type on black (date, title, blurb, optional image) in
// the same corridor rhythm as the works, and it never carries a Work's artist/medium caption.
export type EventItem = {
  kind: "event";
  date: string;
  title: string;
  blurb: string[];
  image?: string;
};

export type RoomItem = Work | WorkGroup | EventItem;

// A logo/visual shown as a round badge (used in the upper-right corner of event cards, here and
// in the timeline popups). Square-ish images fill the circle (the corners fall away); wide ones,
// like a landscape title card, are shown whole inside it instead of being cropped to a sliver.
export function RoundBadge({ src, size }: { src: string; size: number }) {
  // Logos with a flat edge colour: the whole logo is fitted (contain) inside the circle with a
  // small inset, on a background sampled from that edge colour, so it reads as one round mark.
  // Busy patterned images (edges not one flat colour): fill the circle (cover) instead.
  const [bg, setBg] = useState("#000");
  const [pad, setPad] = useState("13%");
  const [fit, setFit] = useState<"contain" | "cover">("contain");
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        background: bg,
        border: "1px solid rgba(255,255,255,0.2)",
        boxSizing: "border-box",
        padding: pad,
      }}
    >
      <img
        src={src}
        alt=""
        onLoad={(e) => {
          const img = e.currentTarget;
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          const wide = w / h > 1.25;
          setPad(wide ? "6%" : "13%");
          try {
            const c = document.createElement("canvas");
            c.width = c.height = 1;
            const ctx = c.getContext("2d");
            if (!ctx) return;
            const px = (x: number, y: number) => {
              ctx.clearRect(0, 0, 1, 1);
              ctx.drawImage(img, x, y, 1, 1, 0, 0, 1, 1);
              return ctx.getImageData(0, 0, 1, 1).data;
            };
            // Sample a little inside each corner so rounded logo corners don't skew the reading.
            const ix = Math.round(w * 0.08);
            const iy = Math.round(h * 0.08);
            const corners = [px(ix, iy), px(w - 1 - ix, iy), px(ix, h - 1 - iy), px(w - 1 - ix, h - 1 - iy)];
            let spread = 0;
            for (let ch = 0; ch < 3; ch++) {
              const vals = corners.map((d) => d[ch]);
              spread = Math.max(spread, Math.max(...vals) - Math.min(...vals));
            }
            if (spread > 40 && !wide) {
              setFit("cover");
              setPad("0");
            } else if (corners[0][3] > 200) {
              setBg(`rgb(${corners[0][0]},${corners[0][1]},${corners[0][2]})`);
            }
          } catch (err) {
            /* keep default black */
          }
        }}
        style={{ display: "block", width: "100%", height: "100%", objectFit: fit }}
      />
    </div>
  );
}

function isGroup(item: RoomItem): item is WorkGroup {
  return (item as WorkGroup).kind === "group";
}

function isEvent(item: RoomItem): item is EventItem {
  return (item as EventItem).kind === "event";
}

// The text card for an event (Black NFT Art, TheBlkChain, IX Shells, ...). On a short screen
// (a phone, or a small browser window) a card with several paragraphs is taller than the room
// left for it and got clipped top and bottom, so it measures itself against the media area it
// sits in and scales down to fit (never below 60%).
// Natural (unscaled) sizes of EVERY event card, measured once by EventProbe. Each card scales to
// fit using the tightest of them, so all event cards share ONE scale -- otherwise only the tall
// card (Proof of Sovereignty) shrank to fit a short window and its text looked smaller than the
// short cards' (Sian: "the copy in the Lady Pheønix card is visibly smaller than ... Diana
// Sinclair's ... we need it to be the same size across").
type EventNatural = { h: number; w: number };
const EventNaturalsContext = createContext<EventNatural[] | null>(null);

function EventCardBlock({
  item,
  isMobile,
  probeRef,
  // The shrink-to-fit machinery below (and the whole point of a shared "naturals" scale) only
  // makes sense when this card sits in a fixed-height box it must not overflow -- true for the
  // scroll-driven corridor's one-slide-per-screen slots. The reduced-motion flat list gave event
  // slides height: auto instead (see that change's own comment), so there is no fixed box to
  // measure against there any more: reading this card's own auto-sized parent's clientHeight
  // just measures the card's OWN rendered size back at itself, a circular reference that made
  // every card settle at a different, unstable scale depending on paint/observer timing -- Sian:
  // "the titles or headers and copy on the event cards on mobile are all over the place." Passing
  // fitToViewport={false} for that render path skips all of this and always shows every card at
  // its real, unscaled size, which the auto-height slide now has room for anyway.
  fitToViewport = true,
}: {
  item: EventItem;
  isMobile: boolean;
  probeRef?: (el: HTMLDivElement | null) => void;
  fitToViewport?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Starts at the safe floor, not 1 -- see the guard inside fit() below for why.
  const [scale, setScale] = useState(fitToViewport ? 0.55 : 1);
  const naturals = useContext(EventNaturalsContext);
  useLayoutEffect(() => {
    if (probeRef) return; // an offscreen measuring copy never scales itself
    if (!fitToViewport) return; // no fixed box to fit -- always full size
    const el = ref.current;
    const parent = el ? el.parentElement : null;
    if (!el || !parent) return;
    const fit = () => {
      // Every real card used to fall back to sizing against ONLY ITSELF (el.offsetHeight)
      // whenever the shared measurement (naturals, from EventProbe) wasn't in yet -- which on
      // a phone, on this card's very first paint, is unreliable: a short card measuring itself
      // alone can land near its full 1x size, when the correct SHARED fit (accounting for the
      // tallest card among them, same reasoning as the Sept 21 "cards must share one size"
      // fix above) is meaningfully smaller. That size mismatch was enough to overflow the
      // slide's fixed-height strip and show only the card's bottom edge on a phone, for
      // however long it took naturals to arrive (Sian: "the image was only showing at the
      // bottom"). Simplest fix: never guess from this card alone -- stay at the safe, always-
      // fully-visible floor (set above) until the real shared measurement is ready, then size
      // properly. naturals normally lands within the same paint (see EventProbe), so in
      // practice this floor is never actually seen -- it's just no longer possible to flash an
      // oversized, cut-off card while waiting for it.
      if (!naturals || !naturals.length) return;
      const restScale = isMobile ? 1 : ROOM_SCALE_MAX; // the media area itself is scaled up at rest
      const availH = parent.clientHeight / restScale - 12;
      // Width fit too: keep a 28px margin each side of what is actually visible (the page
      // width minus any classic scrollbar), so the text and the round logo never touch or
      // pass the window edge, whatever size the window is.
      // (on a wide screen the round logo hangs outside the text column on the right, so leave
      // room for it on both sides to keep the card centred)
      const availW = document.documentElement.clientWidth - 72 - (item.image && !isMobile ? 2 * 104 * restScale : 0);
      let s = 1;
      for (const n of naturals) {
        const fitH = n.h > 0 && availH > 0 ? availH / n.h : 1;
        const fitW = n.w > 0 && availW > 0 ? availW / (n.w * restScale) : 1;
        s = Math.min(s, fitH, fitW);
      }
      setScale(Math.max(0.55, Math.min(1, s)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    ro.observe(el);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [item, isMobile, naturals, probeRef, fitToViewport]);
  return (
    <div ref={(el) => { (ref as { current: HTMLDivElement | null }).current = el; if (probeRef) probeRef(el); }} style={{ position: "relative", maxWidth: "640px", width: isMobile ? "calc(100vw - 80px)" : "100%", padding: isMobile ? "0" : "0 24px", display: "flex", flexDirection: "column", alignItems: "center", transform: !probeRef && scale < 1 ? `scale(${scale})` : undefined, transformOrigin: "center center" }}>
      {/* The card's logo/visual sits round in its upper-right corner. On a wide screen it
          hangs just outside the text column's right edge so long titles can never run
          under it; on a phone it tucks into the corner and the date row reserves its height. */}
      {item.image && (
        <div style={isMobile ? { position: "absolute", top: 0, right: 0 } : { position: "absolute", top: -6, left: "calc(100% + 8px)" }}>
          <RoundBadge src={item.image} size={isMobile ? 56 : 96} />
        </div>
      )}
      <div style={{ fontFamily: MONO_FONT, fontSize: "12px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#888", ...(item.image && isMobile ? { minHeight: 56, paddingRight: 64, boxSizing: "border-box" as const } : {}) }}>
        {item.date}
      </div>
      <h3
        style={{
          fontFamily: "Cormorant Garamond, serif",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: isMobile ? "30px" : "44px",
          fontVariantNumeric: "lining-nums",
          lineHeight: 1.15,
          margin: "16px 0 22px",
          color: "#f2f2f2",
        }}
      >
        {item.title}
      </h3>
      {item.blurb.map((line, i) => (
        <p
          key={i}
          style={{
            // One style for every paragraph of every event card. The first paragraph used to be
            // set as a larger italic "lead", which made cards whose only (long) paragraph came
            // first look bigger than cards with a short lead line -- Sian: "make sure the size
            // of the copy across the cards are consistent".
            fontFamily: "'EB Garamond', serif",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: isMobile ? "17px" : "20px",
            lineHeight: 1.65,
            color: "#d0d0d0",
            margin: "0 0 12px",
          }}
        >
          {line}
        </p>
      ))}
    </div>
  );
}


// Renders every event card once, offscreen and unscaled, and reports each one's natural size.
function EventProbe({ items, isMobile, onMeasure }: { items: EventItem[]; isMobile: boolean; onMeasure: (n: EventNatural[]) => void }) {
  const els = useRef<(HTMLDivElement | null)[]>([]);
  useLayoutEffect(() => {
    if (items.length === 0) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const next = els.current.filter((e): e is HTMLDivElement => !!e).map((e) => ({ h: e.offsetHeight, w: Math.max(e.offsetWidth, e.scrollWidth) }));
      onMeasure(next);
    };
    measure();
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts && fonts.ready) fonts.ready.then(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", measure);
    };
  }, [items, isMobile, onMeasure]);
  if (items.length === 0) return null;
  return (
    <div aria-hidden style={{ position: "fixed", left: -20000, top: 0, width: isMobile ? "100vw" : 1000, visibility: "hidden", pointerEvents: "none" }}>
      {items.map((it, i) => (
        <EventCardBlock key={i} item={it} isMobile={isMobile} probeRef={(el) => { els.current[i] = el; }} />
      ))}
    </div>
  );
}

export type Room = {
  title: string;
  description?: string;
  items: RoomItem[];
  // Overrides the room-title slide's "Room N of M" label with a caller-supplied position --
  // e.g. Proof of Culture's position among ALL 6 timeline milestones, not among the single
  // room this component is ever handed at once (it always gets exactly one room per section,
  // so its own roomIndex/totalRooms are always "1 of 1" there). Omit to keep this component's
  // own numbering, which is what the real multi-room /museum tour still uses.
  positionLabel?: { index: number; total: number };
  // Overrides the room-title slide's "N works" count line (e.g. "5 events" for a room of
  // EventItems). Omit for the default works count.
  countLabel?: string;
};

type Slide =
  | { kind: "room"; room: Room; roomIndex: number; totalRooms: number }
  | {
      kind: "item";
      room: Room;
      item: RoomItem;
      positionInRoom: number;
      roomSize: number;
      // Counting is by individual work (image), not by slide: a cluster of 2 or 4 counts as 2 or 4,
      // and event cards count as none. workStart = works shown before this slide.
      workStart: number;
      roomWorkTotal: number;
    };

// See ProofOfCultureLine.tsx for the same constants and the reasoning (Sept 12,
// seventeenth pass): Neue Montreal Text/Mono are commercial (Pangram Pangram) with no
// license/font files on file yet, so these lead with the real names and fall back to
// General Sans / IBM Plex Mono until @font-face rules for the real fonts exist.
const TEXT_FONT = "'Neue Montreal Text', 'General Sans', Inter, Arial, sans-serif";
const MONO_FONT = "'Neue Montreal Mono', 'IBM Plex Mono', 'Courier New', monospace";

const navButtonStyle: CSSProperties = {
  fontFamily: MONO_FONT,
  background: "transparent",
  border: "none",
  color: "rgba(242,242,242,0.72)",
  fontSize: "10px",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  padding: 0,
  cursor: "pointer",
  textShadow: "0 1px 3px rgba(0,0,0,0.9), 0 1px 10px rgba(0,0,0,0.6)",
};

const WINDOW_RADIUS = 2;
const VIDEO_PLAY_RADIUS = 1.15;

// The centered work image reads noticeably larger than the room title (1.42 vs the room
// title's unchanged 1.15) -- a room title materializing at the same size as an artwork would
// read oddly large for plain text, so only work items get the bump. Bumped up again from 1.28
// per Sian's "bigger still" ask. Hoisted to module scope (not just local to applyTransforms)
// because SlideContent also needs WORK_SCALE_MAX -- see MEDIA_MAX_HEIGHT_PCT below.
const ROOM_SCALE_MAX = 1.15;
const WORK_SCALE_MAX = 1.42;
// A grouped/mosaic slide (several works clustered together) reads as too large at the same
// zoom a single artwork gets -- confirmed as "the grouping of 2... they need to be smaller
// they are taking up the entire page." Mosaics still zoom in slightly as they approach center
// (consistent with everything else in the corridor), just far less than a single work does.
const GROUP_SCALE_MAX = 1.05;
const SCALE_MIN = 0.32;

// The media box used to size itself to 100% of its available height, then applyTransforms
// scaled it up to WORK_SCALE_MAX from there -- with nowhere reserved for that growth to go,
// it overflowed past the top of the corridor's own visible strip and got cropped by the
// ancestor's overflow:hidden, or (worse) grew tall enough to paint over the wordmark/progress
// bar above it (confirmed: "the images are getting cut off at the top while scrolling at full
// screen"). Capping the unscaled media to this percentage of its box, then bottom-aligning it
// (see the .mw-media wrapper's alignItems below), reserves exactly the headroom the zoom needs
// above the image -- at WORK_SCALE_MAX it grows back up to fill the full box with nothing left
// to overflow.
const MEDIA_MAX_HEIGHT_PCT = `${100 / WORK_SCALE_MAX}%`;

// How many individual works (images) an item stands for: a cluster counts each of its members,
// an event card counts none. Sian: "make sure the total count includes every image even in
// cluster of 2 or 4".
function itemWorkCount(item: RoomItem): number {
  if (isEvent(item)) return 0;
  if (isGroup(item)) return item.works.length;
  return 1;
}
function roomWorkCount(room: Room): number {
  return room.items.reduce((n, it) => n + itemWorkCount(it), 0);
}
function worksLabel(n: number): string {
  return `${n} work${n === 1 ? "" : "s"}`;
}

function buildSlides(rooms: Room[]): Slide[] {
  const nonEmpty = rooms.filter((r) => r.items.length > 0);
  const slides: Slide[] = [];
  nonEmpty.forEach((room, roomIndex) => {
    slides.push({ kind: "room", room, roomIndex, totalRooms: nonEmpty.length });
    const roomWorkTotal = roomWorkCount(room);
    let workStart = 0;
    room.items.forEach((item, i) => {
      slides.push({ kind: "item", room, item, positionInRoom: i, roomSize: room.items.length, workStart, roomWorkTotal });
      workStart += itemWorkCount(item);
    });
  });
  return slides;
}

const IPFS_GATEWAYS = [
  "https://dweb.link/ipfs/",
  "https://nftstorage.link/ipfs/",
  "https://w3s.link/ipfs/",
  "https://ipfs.io/ipfs/",
];

function ipfsPath(url?: string): string | null {
  if (!url) return null;
  for (const gw of IPFS_GATEWAYS) {
    if (url.startsWith(gw)) return url.slice(gw.length);
  }
  return null;
}

function withGateway(url: string, index: number): string {
  const path = ipfsPath(url);
  if (path == null) return url;
  return IPFS_GATEWAYS[index % IPFS_GATEWAYS.length] + path;
}

function WalkthroughMedia({
  work,
  allowVideo,
  fit = "contain",
  isMobile = false,
}: {
  work: Work;
  allowVideo: boolean;
  // "cover" is used inside a mosaic grid cell (see MosaicMedia) -- fills its fixed cell instead
  // of being sized/centered as a standalone full image. Default unchanged for single-work slides.
  fit?: "contain" | "cover";
  // On a narrow phone screen, the frame's 80vw width cap binds before the equal-area hFrac
  // math below gets a chance to act -- a landscape work (aspect > ~1.2) ends up both narrower
  // AND shorter than intended, so it visibly reads as "smaller" than a square/portrait
  // neighbor even though the code is trying to equalize their area. There is no way to hit
  // the intended area on a 375px-wide screen without the image running near edge-to-edge, so
  // this widens the mobile cap to match the same "safe full-bleed" width already used for
  // .mw-placeholder (min(88vw, 700px)) elsewhere in this file, closing most of the gap for
  // ordinary landscape aspect ratios while the existing 140cqh cap still guards extreme ones.
  isMobile?: boolean;
}) {
  const [videoGatewayIdx, setVideoGatewayIdx] = useState(0);
  const [imageGatewayIdx, setImageGatewayIdx] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  // The work's real width/height, learned once it loads -- used to size the visible artwork
  // to its own proportions inside the fixed frame below, so the corner radius lands on the
  // ARTWORK'S corners instead of on an invisible letterbox box around it.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  // Sian: "the images seem to be all over the place ... each section should keep them the
  // same size." Previously width/height were "auto", capped by maxWidth/maxHeight -- so a
  // work's displayed size tracked its own original resolution and aspect ratio: a small or
  // unusually-shaped image rendered small, a big square one rendered near the full box, and
  // neighbors in the same room could look wildly different in scale. Giving every work a
  // fixed, definite width AND height (not just an upper bound) with objectFit "contain"
  // means every single-work slide -- in every room -- occupies the exact same footprint,
  // regardless of its source image's own dimensions; a non-matching aspect ratio letterboxes
  // against the dark background instead of shrinking the whole image down.
  // Single-work slides: a fixed-size FRAME (same footprint for every work -- see above) that
  // is a size container, with the artwork inside it fitted to its own aspect ratio using
  // container-query units (cqw/cqh = 1% of the frame's width/height). The radius is on the
  // artwork itself, so corners are visibly rounded whatever the proportions. (Previously the
  // radius sat on the full frame box, whose #111 letterbox is nearly the same colour as the
  // page, so non-matching artwork just looked square-cornered.)
  const aspect = natural && natural.h > 0 ? natural.w / natural.h : 1;
  // Equal visual weight: a square gets the full frame height; wider pieces are pulled in so
  // their AREA matches the square's (height fraction = 1/sqrt(aspect)) instead of all running
  // to the full frame width, and very wide strips are additionally capped at 1.4x the frame
  // height in width (Ancestral Worthiness, ~5.5:1, was blowing up to the whole frame).
  // Portraits are already limited by the frame height itself, so they're unchanged.
  const hFrac = Math.min(1, 1 / Math.sqrt(aspect));
  const frameStyle = {
    width: isMobile ? "min(88vw, 900px)" : "min(80vw, 900px)",
    height: MEDIA_MAX_HEIGHT_PCT,
    margin: "0 auto",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    containerType: "size",
  } as CSSProperties;
  const commonStyle: CSSProperties =
    fit === "cover"
      ? { width: "100%", height: "100%", objectFit: "cover", display: "block" }
      : {
          width: `min(100cqw, 140cqh, calc(100cqh * ${hFrac * aspect}))`,
          height: "auto",
          aspectRatio: `${aspect}`,
          objectFit: "cover",
          display: "block",
          backgroundColor: "#111",
          borderRadius: "10px",
        };
  const frame = (child: JSX.Element): JSX.Element =>
    fit === "cover" ? child : <div style={frameStyle}>{child}</div>;

  const playableVideo = work.animation || (work.mediaType === "video" ? work.media : "") || "";
  const videoSrc = playableVideo ? withGateway(playableVideo, videoGatewayIdx) : "";
  const imageSrc = work.media && work.mediaType !== "video" ? withGateway(work.media, imageGatewayIdx) : "";

  if (allowVideo && !videoFailed && videoSrc) {
    return frame(
      <video
        key={videoSrc}
        src={videoSrc}
        poster={imageSrc || undefined}
        style={commonStyle}
        onLoadedMetadata={(e) => setNatural({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
        // React doesn't always reflect `muted` as the real DOM property, and a browser only
        // autoplays a video that is actually muted -- so set it explicitly and kick off playback.
        onLoadedData={(e) => {
          const el = e.currentTarget;
          el.muted = true;
          el.play().catch(() => {});
        }}
        autoPlay
        muted
        loop
        playsInline
        controls={false}
        preload="metadata"
        onError={() => {
          if (ipfsPath(playableVideo) && videoGatewayIdx < IPFS_GATEWAYS.length - 1) {
            setVideoGatewayIdx((i) => i + 1);
          } else {
            setVideoFailed(true);
          }
        }}
      />
    );
  }

  if (!imageFailed && imageSrc) {
    return frame(
      <img
        key={imageSrc}
        src={imageSrc}
        alt={`${work.title} by ${work.artist}`}
        style={commonStyle}
        onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        onError={() => {
          if (ipfsPath(work.media) && imageGatewayIdx < IPFS_GATEWAYS.length - 1) {
            setImageGatewayIdx((i) => i + 1);
          } else {
            setImageFailed(true);
          }
        }}
      />
    );
  }

  if (playableVideo && !allowVideo) {
    return <div className="mw-placeholder">Video</div>;
  }

  return <div className="mw-placeholder">{work.media || work.animation ? "Image Unavailable" : "Loading"}</div>;
}

// A small grid of images for a WorkGroup, filling the same footprint a single image would.
// Columns scale with count -- up to 4 works get one clean row (so a group of 3, like the
// community access tokens, doesn't wrap into an awkward 2-then-1 layout), 5+ wraps to 3-wide --
// deliberately a stylized cluster rather than each piece shown at its own aspect ratio, since
// the point is to read as one set.
function MosaicMedia({ group, allowVideo, isMobile }: { group: WorkGroup; allowVideo: boolean; isMobile: boolean }) {
  const n = group.works.length;
  // Phone only: a group of 3 (the community access tokens) would otherwise pick a tall single
  // column of three small squares. Lay it out as a true mosaic instead -- two across on top,
  // the third centred beneath them.
  const trio = isMobile && n === 3;
  const GAP = 12;
  // Room reserved under each row for the piece's own title (up to two 13px lines + margin).
  const TITLE_ROW = 44;
  // Widest a cluster is allowed to get (a single row of 4 on a big window).
  const MAX_WIDTH = 1400;
  // The cluster is sized to FIT its frame, measured in real pixels (the frame is a size
  // container with a fixed share of the corridor's media box). Sian: "the clusters of 4 are too
  // small and the titles are hidden" -- the old version always used a fixed 2-column layout in a
  // frame only 70% of the box tall, so on a wide window the tiles were tiny. Now the layout
  // itself adapts: every column count from 1..n is tried, and the one that gives the LARGEST
  // square tile in this window wins (a single row of 4 on a wide screen, 2x2 on a tall/narrow
  // one, 2 across for a pair, etc.), always leaving room for each tile's own title underneath.
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
  }, []);

  let cols = Math.min(n, 2);
  let side = 0;
  if (box && box.w > 0 && box.h > 0) {
    let best = -1;
    for (let c = 1; c <= n; c++) {
      const r = Math.ceil(n / c);
      const byHeight = (box.h - (r - 1) * GAP - r * TITLE_ROW) / r;
      const byWidth = (Math.min(box.w, MAX_WIDTH) - (c - 1) * GAP) / c;
      const candidate = Math.min(byHeight, byWidth);
      if (candidate > best) {
        best = candidate;
        cols = c;
      }
    }
    side = Math.max(60, Math.floor(best));
    if (trio) {
      cols = 2;
      const rows = 2;
      const byHeight = (box.h - (rows - 1) * GAP - rows * TITLE_ROW) / rows;
      const byWidth = (Math.min(box.w, MAX_WIDTH) - GAP) / 2;
      side = Math.max(60, Math.floor(Math.min(byHeight, byWidth)));
    }
  }
  const frameStyle = {
    width: "100%",
    // Groups only zoom to GROUP_SCALE_MAX, so they need far less headroom than a single work
    // (which reserves 1/1.42 of the box) -- this gives them nearly the whole box.
    height: `${100 / GROUP_SCALE_MAX}%`,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    containerType: "size",
  } as CSSProperties;
  return (
    <div ref={frameRef} style={frameStyle}>
      {side > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, ${side}px)`,
            gap: `${GAP}px`,
            justifyContent: "center",
          }}
        >
          {group.works.map((w, i) => (
            // Every piece carries its own title directly beneath it (always visible) -- Sian:
            // "each pfp should have its own title/name and then below the project name, date".
            // The shared project/date caption is rendered by SlideContent below.
            <div key={i} className="mw-mosaic-cell" style={{ position: "relative", minWidth: 0, ...(trio && i === 2 ? { gridColumn: "1 / -1", justifySelf: "center", width: side } : {}) }}>
              <div style={{ aspectRatio: "1 / 1", borderRadius: "7px", overflow: "hidden", background: "#111" }}>
                <WalkthroughMedia work={w} allowVideo={allowVideo} fit="cover" />
              </div>
              <div className="mw-mosaic-title">{w.title}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ", 2022" (or ", 2021, 2022" if a cluster spans years) for the shared project caption.
function groupYearSuffix(group: WorkGroup): string {
  const years = Array.from(new Set(group.works.map((w) => w.year).filter(Boolean))).sort();
  return years.length ? `, ${years.join(", ")}` : "";
}

function groupArtistLine(group: WorkGroup): string {
  const artists = Array.from(new Set(group.works.map((w) => w.artist)));
  return artists.length === 1 ? artists[0] : "Various artists";
}

function SlideContent({
  slide,
  isMobile,
  allowVideo,
  isArrival = false,
  startHidden = false,
  // See EventCardBlock's own comment: only the scroll-driven corridor's fixed-height slides need
  // an event card to shrink-to-fit. Defaults true for that path; the reduced-motion flat list
  // passes false.
  fitToViewport = true,
}: {
  slide: Slide;
  isMobile: boolean;
  allowVideo: boolean;
  isArrival?: boolean;
  // Scroll-driven corridor only: render a work's media/caption at opacity 0 on first paint.
  // applyTransforms writes the real opacity right after mount, but until then every work slide
  // painted fully visible -- and the first work after a room title (which fades via a slow CSS
  // transition) then took ~1s to fade back OUT, showing as a brief image flash over the room
  // title's text ("the first image flashes quickly and then disappears").
  startHidden?: boolean;
  fitToViewport?: boolean;
}) {
  if (slide.kind === "room") {
    // Proof of Culture always hands this component exactly one room per section, so its own
    // roomIndex/totalRooms are always "1 of 1" here -- not useful, and confirmed confusing
    // ("we dont need the room 1 of 1 on each page"). Use the caller-supplied positionLabel
    // when given (Proof of Culture's actual position among all 6 timeline milestones); fall
    // back to this component's own numbering for the real multi-room /museum tour.
    const posIndex = slide.room.positionLabel ? slide.room.positionLabel.index : slide.roomIndex;
    const posTotal = slide.room.positionLabel ? slide.room.positionLabel.total : slide.totalRooms;
    return (
      <div style={{ width: "100%" }}>
        <div
          style={{
            fontFamily: MONO_FONT,
            fontSize: "11px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#888",
            marginBottom: "18px",
          }}
        >
          Room {posIndex + 1} of {posTotal}
        </div>
        <h2
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontWeight: 400,
            fontSize: isMobile ? "36px" : "64px",
            margin: 0,
            fontStyle: "italic",
            color: "#f2f2f2",
          }}
        >
          {slide.room.title}
        </h2>
        {slide.room.description && (
          <p style={{ maxWidth: "520px", width: "100%", margin: "24px auto 0", color: "#bbb", fontSize: "14px", lineHeight: 1.8 }}>
            {slide.room.description}
          </p>
        )}
        <div style={{ fontFamily: MONO_FONT, marginTop: "30px", fontSize: "12px", color: "#777", letterSpacing: "0.05em" }}>
          {slide.room.countLabel ?? worksLabel(roomWorkCount(slide.room))}
        </div>
        {/* Sian: "Add scroll to navigate on each title page" -- the corridor already responds
            to scroll/swipe/keys everywhere, this slide just never told anyone that. Same
            visual language as the threshold page's own "Scroll to begin" cue. */}
        <div className="mw-scroll-cue" style={{ fontFamily: MONO_FONT, marginTop: "36px", fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#666" }}>
          Scroll to continue ↓
        </div>
      </div>
    );
  }

  const item = slide.item;

  if (isEvent(item)) {
    // Type on black: the block sits centered in the media area (not bottom-anchored like an
    // image) and the small position line takes the caption slot, so it fades/scales with the
    // same .mw-media / .mw-caption machinery the works use.
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", flex: "1 1 auto", minHeight: 0 }}>
        <div
          className={isArrival ? "mw-media mw-media-arrival" : "mw-media"}
          style={{ flex: "1 1 auto", minHeight: 0, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", ...(startHidden ? { opacity: 0 } : {}) }}
        >
          <EventCardBlock item={item} isMobile={isMobile} fitToViewport={fitToViewport} />
        </div>
        <div className={isArrival ? "mw-caption mw-caption-arrival" : "mw-caption"} style={{ marginTop: "20px", flexShrink: 0, ...(startHidden ? { opacity: 0 } : {}) }}>
          <div style={{ fontFamily: MONO_FONT, color: "#666", fontSize: "11px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {slide.room.title} · Event
          </div>
        </div>
      </div>
    );
  }

  const group = isGroup(item) ? item : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", flex: "1 1 auto", minHeight: 0 }}>
      <div
        className={isArrival ? "mw-media mw-media-arrival" : "mw-media"}
        style={{ flex: "1 1 auto", minHeight: 0, width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", ...(startHidden ? { opacity: 0 } : {}) }}
      >
        {group ? (
          <MosaicMedia group={group} allowVideo={allowVideo} isMobile={isMobile} />
        ) : (
          <WalkthroughMedia work={item as Work} allowVideo={allowVideo} isMobile={isMobile} />
        )}
      </div>
      <div className={isArrival ? "mw-caption mw-caption-arrival" : "mw-caption"} style={{ marginTop: "20px", flexShrink: 0, ...(startHidden ? { opacity: 0 } : {}) }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontWeight: 500, fontSize: "19px", letterSpacing: "0.01em", color: "#f2f2f2" }}>
          {group ? group.label : (item as Work).title}
          {group ? groupYearSuffix(group) : (item as Work).year ? `, ${(item as Work).year}` : ""}
        </div>
        <div style={{ fontFamily: MONO_FONT, color: "#bbb", fontSize: "13px", marginTop: "6px", fontStyle: "normal" }}>
          {group ? groupArtistLine(group) : (item as Work).artist}
        </div>
        {!group && ((item as Work).medium || (item as Work).collection) && (
          <div style={{ fontFamily: MONO_FONT, color: "#999", fontSize: "12px", marginTop: "8px", letterSpacing: "0.02em" }}>
            {[(item as Work).medium, (item as Work).collection].filter(Boolean).join(" · ")}
          </div>
        )}
        <div style={{ fontFamily: MONO_FONT, color: "#666", fontSize: "11px", marginTop: "10px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
          {slide.room.title} · {group ? `${slide.workStart + 1}–${slide.workStart + group.works.length}` : slide.workStart + 1} / {slide.roomWorkTotal}
          {group ? ` · ${group.works.length} works` : ""}
        </div>
      </div>
    </div>
  );
}

export default function MuseumWalkthrough({
  rooms,
  onExit,
  onNext,
  nextLabel,
  onPrev,
  prevLabel,
  scrollUnitFactor = 0.75,
  softEntry = false,
}: {
  rooms: Room[];
  onExit?: () => void;
  // Continues straight into the next section instead of returning to the timeline -- see
  // ProofOfCultureLine's goToNext. Omit either prop to leave the corridor exactly as before
  // (Exit only), which is what the real multi-room /museum route still does.
  onNext?: () => void;
  nextLabel?: string;
  // Same idea, one room back -- see ProofOfCultureLine's goToPrev. Omit to leave the corridor
  // without a Back option (the real /museum tour still doesn't pass this).
  onPrev?: () => void;
  prevLabel?: string;
  // How much scroll distance one work takes, as a fraction of the window height. Larger =
  // slower, more deliberate scrolling. Default matches the original /museum tour; Proof of
  // Culture passes a bigger value ("images are scrolling too fast").
  scrollUnitFactor?: number;
  // Proof of Culture only. Softens how a section opens: the corridor fades up from black on
  // mount, and a room's first work fades/grows in along the scroll instead of staying near-
  // invisible until the last moment and then popping ("the first image just appears out of
  // nowhere"). Off by default so the real /museum tour is unchanged.
  softEntry?: boolean;
}) {
  const navigate = useNavigate();
  const exit = onExit || (() => navigate("/"));
  const slides = useMemo(() => buildSlides(rooms), [rooms]);
  const eventItems = useMemo(() => rooms.reduce<EventItem[]>((acc, r) => acc.concat(r.items.filter(isEvent) as EventItem[]), []), [rooms]);
  const [eventNaturals, setEventNaturals] = useState<EventNatural[] | null>(null);
  // The Index (jump-to-room) nav only makes sense when there's more than one room in this
  // tour -- Proof of Culture always passes a single section's room, where "jump to a room"
  // would just point back at the room already on screen. Keep the feature (still useful on
  // the full multi-room /museum route) but hide the button/overlay entirely when there's
  // nothing for it to navigate between.
  const multiRoom = rooms.filter((r) => r.items.length > 0).length > 1;
  const [isMobile, setIsMobile] = useState(false);
  const [showIndex, setShowIndex] = useState(false);
  const [jumpVeilVisible, setJumpVeilVisible] = useState(false);
  const [centerIndex, setCenterIndex] = useState(0);
  // True once the visitor has scrolled to the bottom of the room. Used (with centerIndex) to
  // pulse the Next link. A plain document-bottom check, because on real phones the dynamic
  // toolbar can keep the corridor's progress a hair short of the last slide, and the
  // reduced-motion layout doesn't drive centerIndex at all (Sian, Oct 14: no pulse on mobile).
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    function check() {
      const doc = document.documentElement;
      const bottom = window.scrollY + window.innerHeight;
      setAtEnd(doc.scrollHeight > window.innerHeight + 40 && bottom >= doc.scrollHeight - 120);
    }
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);
  const [progressPct, setProgressPct] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  // Proof of Culture only (softEntry). Whether the corridor's content wrapper is faded in yet.
  // See the effect below for why this is driven from state/rAF/a safety timeout instead of a
  // CSS @keyframes animation.
  const [softVisible, setSoftVisible] = useState(!softEntry);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const progressRef = useRef(0);
  // The scroll listener writes the input-derived target here; a separate easing loop moves
  // progressRef (the value everything else reads) toward it frame by frame, so raw scroll bursts
  // don't show up 1:1 as bursts of visual movement. See the easing effect below.
  const rawProgressRef = useRef(0);
  const unitRef = useRef(600);
  const rafRef = useRef<number | null>(null);
  const smoothRafRef = useRef<number | null>(null);
  // Set while the scroll input is jumping by more than a fraction of a slide in a single tick
  // (a fast trackpad fling) and cleared a short beat after it settles. Exists because the
  // room-entry "arrival" media/caption have their own CSS transition-delay (see
  // .mw-media-arrival / .mw-caption-arrival) tuned for a normal-speed approach -- during a fast
  // fling, the JS-computed opacity target can rise to its peak and fall back again faster than
  // that delay + transition can visually catch up, so the first work after a room (confirmed:
  // THIRD EYE GLO.) never appears to render at all even though its own progress math swept
  // through it correctly. See applyTransforms, which reads this to bypass that CSS delay only
  // while a fling is in progress.
  const fastScrollRef = useRef(false);
  const fastScrollTimeoutRef = useRef<number | null>(null);
  // Sian confirmed the fix above (fastScrollRef, zeroing the CSS transition during a fling)
  // still didn't stop THIRD EYE GLO. from getting skipped on her real trackpad -- so rather than
  // re-tuning that same timing-race heuristic again, this is a structurally different,
  // speed-independent backstop. Instead of trying to make the *visual* transition win a race
  // against however fast the scroll input happens to be, it clamps the *target itself*: no
  // matter how hard a single fling tries to fly past a room's first work in one tick, the raw
  // scroll target gets pinned there and held for a fixed real-world duration first. See its use
  // in the scroll effect and in the easing tick() below.
  const holdIndexRef = useRef<number | null>(null);
  const holdUntilRef = useRef<number>(0);
  // A single fast fling can skip past MORE than one work in one tick, but the loop below only
  // ever pins and holds the FIRST one it finds (see its own comment) -- the rest of that tick's
  // real scroll distance is simply not acted on yet. Normally that's fine: the next genuine
  // scroll event re-evaluates from wherever the real input has moved on to, and the same check
  // catches the next skipped work in turn. But if the fling has already fully settled by the
  // time the hold above ends -- no more real scroll events are coming, because the input isn't
  // moving anymore -- nothing would ever re-run onScroll, and every work after the first one
  // this fling skipped would be left permanently stuck at whatever near-zero opacity/scale it
  // mounted with. This timer forces exactly one more onScroll() pass right as each hold expires,
  // using whatever the real scroll position is by then, so a big multi-work skip resolves as an
  // automatic one-hold-per-work catch-up instead of freezing after the first.
  const holdTimeoutRef = useRef<number | null>(null);
  // Cumulative scroll-distance offset at the start of each slide. A room-title slide gets a
  // longer distance than a work slide (ROOM_WEIGHT), so crossing into a new room takes a
  // deliberately slower, held beat -- room to recede, a moment of near-darkness, the title to
  // emerge -- rather than every slide, room title or artwork, consuming the same brisk pace.
  const offsetsRef = useRef<number[]>([0]);
  const [totalHeight, setTotalHeight] = useState(600);

  // The corridor measures scroll progress through its own content as `window.scrollY` minus
  // this wrapper's absolute position on the page (see the `onScroll` effect below) -- it never
  // resets scrollY itself, it just reads whatever the browser already has. Mounting here always
  // follows the intro copy page, which (on phones) is a normal scrollable page of its own --
  // reading a few paragraphs to the bottom can leave window.scrollY several hundred pixels deep
  // by the time it hands off. Without a reset, this corridor's very first progress read (below,
  // `onScroll()` runs once immediately on mount) inherits that leftover scroll as if the visitor
  // had already scrolled that far into THIS content, landing centered on the wrong slide -- or
  // in the dead, near-black gap between two slides -- instead of slide 0. A plain layout effect,
  // guaranteed to run before that first onScroll() (React flushes all useLayoutEffects, in
  // order, ahead of any useEffect), fixes the starting point regardless of how the previous
  // page left the scroll position.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // The corridor used to fade up from black via a CSS @keyframes animation (mwSoftEnter, applied
  // through the .mw-soft class) with animation-fill-mode: both. Reported bug (screen recording
  // confirmed): on at least one real device, across two different browsers, that animation never
  // fires/completes, and "both" then holds the *from* keyframe (opacity: 0) forever -- the whole
  // corridor content wrapper stays invisible indefinitely while sibling chrome (logo, Next/Exit,
  // progress bar, which live outside this wrapper) renders fine. Drive the fade from React state
  // instead, with a hard safety-net timeout, so the content can never get stuck invisible even if
  // the browser never runs the transition at all.
  useEffect(() => {
    if (!softEntry) return;
    setSoftVisible(false);
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSoftVisible(true));
    });
    const safety = window.setTimeout(() => setSoftVisible(true), 1800);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(safety);
    };
  }, [softEntry]);

  useEffect(() => {
    document.title = "Proof of Culture Exhibit | MoDDA";
  }, []);

  // The wordmark fades out after a few idle seconds and returns on mouse movement (or a touch,
  // on mobile) -- the interface should get out of the way of the art rather than sit on screen
  // permanently. Index/Exit stay put (see their own JSX below, which no longer ties opacity to
  // chromeVisible) -- people need to be able to find their way out at a glance, including
  // right at the end of a room where they'd actually want it.
  useEffect(() => {
    let idleTimer: number | undefined;
    function showChrome() {
      setChromeVisible(true);
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => setChromeVisible(false), 3500);
    }
    showChrome();
    window.addEventListener("mousemove", showChrome);
    window.addEventListener("touchstart", showChrome);
    return () => {
      window.clearTimeout(idleTimer);
      window.removeEventListener("mousemove", showChrome);
      window.removeEventListener("touchstart", showChrome);
    };
  }, []);

  function applyTransforms() {
    const progress = progressRef.current;
    const xSpread = Math.min(window.innerWidth * 0.5, 480);

    // Two rhythms live in this corridor. Moving between two works in the same room is quick and
    // direct -- you're browsing. Crossing into a new room is a threshold: the previous room
    // recedes, there's a beat of near-darkness, then the room title emerges before the first work
    // of the new room arrives. Room-adjacent items (the room title itself, the work just before
    // it, and the first work after it) get a wider range and a steeper opacity curve, so they
    // spend real scroll distance sitting near-invisible instead of tracking scroll like ordinary
    // neighbors -- that reads as "communicating a threshold," not just moving faster or slower.
    const WORK_OUTGOING_RANGE = 1.4;
    const WORK_INCOMING_RANGE = 1.6;
    // Both kept under 1 index-unit on purpose -- see the comment above applyTransforms's caller
    // sites for why: a range over 1 lets a room-adjacent item still be partway visible once its
    // neighbor (exactly 1 index away) has already taken over, which is what caused the ghosting.
    const ROOM_OUTGOING_RANGE = 0.78;
    // softEntry: the first work starts arriving later (0.72 instead of 0.9) so it doesn't fade in
    // on top of the still-visible room title -- the two used to show together as a muddy overlap.
    const ROOM_INCOMING_RANGE = softEntry ? 0.72 : 0.9;

    itemRefs.current.forEach((el, i) => {
      const slide = slides[i];
      const isRoomTitle = slide?.kind === "room";
      const roomAdjacent = isRoomTitle || slides[i - 1]?.kind === "room" || slides[i + 1]?.kind === "room";

      const distance = i - progress;
      const abs = Math.abs(distance);
      const outgoing = distance < 0;
      const range = roomAdjacent
        ? (outgoing ? ROOM_OUTGOING_RANGE : ROOM_INCOMING_RANGE)
        : (outgoing ? WORK_OUTGOING_RANGE : WORK_INCOMING_RANGE);
      const t = Math.min(abs / range, 1);
      // Opacity has its own, tighter range capped at 1 index-unit: the scale range (1.4/1.6 for
      // plain work-to-work movement) is wider so the swell keeps its gentle curve, but that same
      // wide range meant a work exactly one slide away (the neighbor sitting beside the centered
      // one, at rest, abs === 1) never fully reached t === 1 and so never fully reached opacity 0
      // -- it sat around 15-20% opacity, faintly visible on either side of the centered work.
      // Room-adjacent ranges (0.78/0.9) were already under 1, so this only changes plain work
      // neighbors. Sian: "we should not be able to see them on either side."
      const opacityRange = Math.min(range, 1);
      const tOpacity = Math.min(abs / opacityRange, 1);

      // Ease rather than move in lockstep with scroll position. A steeper exponent holds an item
      // near-invisible for most of its approach and lets it swing up only right at the end --
      // that's the "emerges from darkness" feel for a room title or a room's first work. Plain
      // work-to-work movement keeps the gentler, quicker curve already in place.
      const scaleExp = roomAdjacent ? (outgoing ? 2.4 : softEntry ? 1.8 : 2.7) : (outgoing ? 2.0 : 2.35);
      const opacityExp = roomAdjacent ? (outgoing ? 2.6 : softEntry ? 1.7 : 3.2) : (outgoing ? 1.5 : 1.75);

      const scaleEase = 1 - Math.pow(1 - t, scaleExp);
      const opacityEase = 1 - Math.pow(1 - tOpacity, opacityExp);
      // Room-adjacent items barely move sideways at all -- crossing a room boundary should read
      // as a fade out and in, not a slide, so the outgoing work, the room title, and the
      // incoming first work all stay close to center and let opacity/scale carry the transition.
      // Plain work-to-work movement keeps its existing slide.
      const isGroupItem = !isRoomTitle && slide?.kind === "item" && isGroup(slide.item);
      // Text slides only swell as much as a room title does -- scaling paragraphs to an
      // artwork's 1.42x would blow the type up.
      const isEventItem = !isRoomTitle && slide?.kind === "item" && isEvent(slide.item);
      // On a phone-width screen a text card can't swell past 1x: at 1.15x its 540px column
      // grew to ~620px inside a 600px window and the sides of the text were cut off.
      // Works and groups swell as they arrive (up to 1.42x / 1.05x), and their frame is 80vw wide,
      // so on a narrow screen that growth pushed the artwork to within ~20px of the window edge
      // (and past it for groups). On a phone-width screen cap the swell so at least 32px of
      // dark margin is left on each side.
      const narrow = window.innerWidth <= 768; // read live: the rAF loop holds a first-render closure, where the isMobile state is still false
      // Size the cap from the media box's real (untransformed) layout width, not an assumed 80vw:
      // the frame can be wider than that on a phone (measured ~337px in a 375px window).
      const capMedia = el.querySelector<HTMLElement>(".mw-media");
      const capW = capMedia && capMedia.offsetWidth > 0 ? capMedia.offsetWidth : 0.8 * window.innerWidth;
      const widthCap = narrow ? Math.max(0.6, (window.innerWidth - 64) / capW) : Infinity;
      const scaleMax = isEventItem
        ? (narrow ? 1 : ROOM_SCALE_MAX)
        : isRoomTitle
          ? ROOM_SCALE_MAX
          : isGroupItem
            ? Math.min(GROUP_SCALE_MAX, widthCap)
            : Math.min(WORK_SCALE_MAX, widthCap);
      const xMult = roomAdjacent ? 0.12 : outgoing ? 1.08 : 0.94;

      const x = distance * xSpread * xMult;
      const scale = scaleMax - scaleEase * (scaleMax - SCALE_MIN);
      const opacity = Math.max(0, 1 - opacityEase);

      el.style.zIndex = String(1000 - Math.round(abs * 10));
      el.style.pointerEvents = abs < 0.5 ? "auto" : "none";

      if (isRoomTitle) {
        // A room slide is one fading unit -- title, eyebrow, description and count all move
        // together -- so the whole wrapper (position + scale + opacity) animates as one block.
        // Unchanged.
        el.style.transform = `translate3d(${x}px, 0, 0) scale(${scale})`;
        el.style.opacity = String(opacity);
      } else {
        // A work slide used to scale this whole wrapper -- media and caption stacked together --
        // up to WORK_SCALE_MAX. That made the combined box taller than the fixed-height strip it
        // sits in (the ancestor clips overflow), so the caption, sitting below the media in the
        // stack, got pushed past the visible edge and disappeared -- at a different point for
        // every work depending on how tall its own caption happened to be (a two-line title, or
        // the mosaic's extra "N works" line, made it worse; confirmed as "at its largest the size
        // is all over the place, you can't see the title and metadata"). Now only the media
        // element scales up for the "centered artwork reads larger" effect; the wrapper only
        // translates (no scale), and the caption is never scaled or moved, so title/artist/
        // metadata stay fully visible in a consistent place under every work.
        el.style.transform = `translate3d(${x}px, 0, 0)`;
        el.style.opacity = "1";
        const mediaEl = el.querySelector<HTMLElement>(".mw-media");
        const captionEl = el.querySelector<HTMLElement>(".mw-caption");
        // During a fast fling, force these writes through instantly instead of letting the
        // CSS transition (and, for the arrival variants, its transition-delay) smooth them --
        // otherwise a value that rises to its peak and falls back within less than the
        // delay+duration never visibly appears. Restored to the normal CSS-driven softness the
        // instant scrolling settles again, so ordinary browsing is untouched. See fastScrollRef.
        if (mediaEl) {
          mediaEl.style.transitionDuration = fastScrollRef.current ? "0ms" : "";
          mediaEl.style.transitionDelay = fastScrollRef.current ? "0ms" : "";
          mediaEl.style.opacity = String(opacity);
          // Anchor the scale to the media box's own bottom edge (where the caption starts)
          // instead of its center, for an IMAGE (or mosaic) -- center-anchored growth pushed the
          // image downward into the caption below it at large scale, covering the title/artist
          // line even though the caption itself was never touched -- confirmed as "the titles
          // are getting covered." Bottom-anchoring means the image only grows upward and
          // sideways, so its bottom edge never moves past where the caption begins.
          //
          // An event card's own content is laid out CENTERED inside this same .mw-media box
          // (see SlideContent's isEvent branch: "the block sits centered in the media area --
          // not bottom-anchored like an image"), not bottom-aligned like an image's -- so
          // scaling it from the box's bottom edge, while it's still small early in its arrival
          // (scale well under 1), pushed the card down against the bottom of the box instead of
          // keeping it centered, which on a phone (a tall, mostly-empty .mw-media box) meant the
          // card start visible only near the very bottom of the screen, cut off above, until
          // scale finally reached ~1 and it filled the box enough to look centered again (Sian,
          // both after the previous fix and again on retest: "the image was only showing at the
          // bottom" / "same thing"). Event cards scale from their own center instead, matching
          // how they're actually laid out.
          mediaEl.style.transformOrigin = isEventItem ? "50% 50%" : "50% 100%";
          mediaEl.style.transform = `scale(${scale})`;
        }
        if (captionEl) {
          captionEl.style.transitionDuration = fastScrollRef.current ? "0ms" : "";
          captionEl.style.transitionDelay = fastScrollRef.current ? "0ms" : "";
          captionEl.style.opacity = String(opacity);
        }
      }
    });
  }

  useEffect(() => {
    if (reducedMotion) return;

    const ROOM_WEIGHT = 2.2;

    function computeUnit() {
      const next = Math.max(Math.round(420 * (scrollUnitFactor / 0.75)), Math.round(window.innerHeight * scrollUnitFactor));
      unitRef.current = next;

      const offsets: number[] = [0];
      for (let i = 0; i < slides.length; i++) {
        const weight = slides[i].kind === "room" ? ROOM_WEIGHT : 1;
        offsets.push(offsets[i] + weight * next);
      }
      offsetsRef.current = offsets;
      setTotalHeight(offsets[offsets.length - 1] || next);
    }
    computeUnit();

    // How much of a work-to-work segment (in fractional index units, 0..1) is spent "snapped" --
    // held near the centered image at rest -- before scrolling further releases it toward the
    // next work. Symmetric on both ends of the segment, so the incoming work also settles into
    // its own snap once it arrives, rather than drifting past center.
    const SNAP_HOLD = 0.18;

    function smootherstep(t: number) {
      // Zero first *and* second derivative at both ends (Ken Perlin's improved smoothstep) --
      // that's what makes the release out of the snap point feel like it eases open rather than
      // lurching, and the arrival into the next snap point feel like it settles rather than
      // stopping short.
      return t * t * t * (t * (t * 6 - 15) + 10);
    }

    function snapWarp(raw: number) {
      const i = Math.floor(raw);
      if (i < 0 || i >= slides.length - 1) return raw;
      // Leave any segment touching a room slide alone -- that transition is already tuned
      // (recede / darkness / title / arrival) and isn't part of this ask.
      if (slides[i]?.kind === "room" || slides[i + 1]?.kind === "room") return raw;

      const f = raw - i;
      if (f <= SNAP_HOLD) return i;
      if (f >= 1 - SNAP_HOLD) return i + 1;
      const t = (f - SNAP_HOLD) / (1 - 2 * SNAP_HOLD);
      return i + smootherstep(t);
    }

    function onScroll() {
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const top = wrapper.getBoundingClientRect().top + window.scrollY;
        const y = Math.max(0, window.scrollY - top);
        const offsets = offsetsRef.current;
        let i = 0;
        while (i < offsets.length - 2 && y >= offsets[i + 1]) i++;
        const segStart = offsets[i];
        const segEnd = offsets[i + 1] ?? segStart + unitRef.current;
        const segLen = Math.max(1, segEnd - segStart);
        const raw = i + Math.min(1, (y - segStart) / segLen);
        if (!Number.isFinite(raw)) return;
        const clamped = Math.max(0, Math.min(slides.length - 1, raw));
        const total = offsets[offsets.length - 1] || 1;
        const now = performance.now();

        // Currently holding a room's first work in place -- ignore this tick's scroll target
        // entirely (the progress bar still tracks real scroll position) until the hold window
        // elapses, so that item gets a genuine, uninterrupted beat on screen no matter how much
        // further scrolling arrives during the hold.
        if (holdIndexRef.current != null) {
          if (now < holdUntilRef.current) {
            setProgressPct(Math.max(0, Math.min(1, y / total)));
            return;
          }
          holdIndexRef.current = null;
        }

        // If this tick's jump would carry the target straight over ANY work/event index
        // without ever landing there -- clamp the target to the first skipped index instead and
        // start the forced hold above. FORCED_HOLD_MS is generous on purpose: unlike the
        // fastScrollRef race, this doesn't depend on winning against scroll speed, so it's fine
        // to be a real, deliberate beat.
        //
        // Two separate protections, not one:
        //  1) A room's first work always gets a guaranteed beat on arrival, even from a normal
        //     single-step scroll (this is the original, long-working "THIRD EYE GLO. comes in
        //     too fast" fix -- a deliberate pause when you first walk into a room).
        //  2) ANY item -- not just a room's first -- gets a hold when this tick's jump would
        //     skip PAST it without ever landing there (i.e. more than one new slide index is
        //     reached within a single scroll tick: `hi > lo` below). That's the real, general
        //     bug: an item skipped this way is never rendered as "current" for a full frame, so
        //     its mount/arrival transform can be left stuck mid-animation forever.
        //
        // Sept 22 first attempt: widened rule #1 to fire on EVERY item, not just room-first --
        // this did catch "Boredom" (2nd work in THE BEGINNING, stuck black after a fast flick,
        // the same THIRD EYE GLO. symptom) but was far too broad: it forced a 1s hold on every
        // single ordinary forward step anywhere in the corridor, which read to Sian as the whole
        // thing freezing/going black ("only seeing the nav card, nothing else, scrolling
        // black"). Replaced same day with the `hi > lo` skip check instead, which only fires on
        // a genuine multi-index jump (rare -- true fast flicks), leaving ordinary one-step
        // scrolling through non-room-first items exactly as fast and smooth as it always was.
        //
        // Forward direction only (Sian: scrolling back out of a room made the hold read as
        // "disappears then appears suddenly" -- clunky). Blowing straight past a work only
        // happens when arriving at it, so the hold only needs to guard that direction; leaving
        // backward scrolling alone restores its previous, already-fine behavior.
        // Slowed from 550ms / 0.5 damp ("THIRD EYE GLO. comes in too fast"): the snap-in is now a
        // gentler ~0.4s ease followed by a ~1s hold.
        const FORCED_HOLD_MS = 1000;
        const prevRaw = rawProgressRef.current;
        let stopAt: number | null = null;
        if (clamped > prevRaw) {
          const lo = Math.ceil(prevRaw);
          const hi = Math.floor(clamped);
          const skippedMultiple = hi > lo;
          for (let idx = lo; idx <= hi; idx++) {
            if (idx === prevRaw) continue;
            if (idx <= 0) continue;
            const isRoomFirstWork = slides[idx]?.kind !== "room" && slides[idx - 1]?.kind === "room";
            const isGenuineSkip = skippedMultiple && slides[idx]?.kind !== "room";
            if (isRoomFirstWork || isGenuineSkip) {
              stopAt = idx;
              break;
            }
          }
        }
        if (stopAt != null) {
          rawProgressRef.current = stopAt;
          holdIndexRef.current = stopAt;
          holdUntilRef.current = now + FORCED_HOLD_MS;
          if (holdTimeoutRef.current != null) window.clearTimeout(holdTimeoutRef.current);
          holdTimeoutRef.current = window.setTimeout(() => {
            holdTimeoutRef.current = null;
            onScroll();
          }, FORCED_HOLD_MS + 30);
          setProgressPct(Math.max(0, Math.min(1, y / total)));
          return;
        }

        // A jump of more than a third of a slide within one scroll-driven tick means the input
        // is moving fast enough that a room-arrival item's CSS transition-delay could outlast
        // the whole time its computed opacity spends near its peak -- see fastScrollRef above
        // and applyTransforms below. Track it here, and let it settle back to "normal" a short
        // beat (260ms, matching the arrival transition-delay) after input stops jumping like
        // that, rather than clearing it immediately on the first small/slow tick. Kept alongside
        // the hard hold above rather than removed -- harmless, and still helps ordinary (non
        // room-adjacent) fast scrolling elsewhere in the corridor.
        const FAST_SCROLL_DELTA = 0.35;
        if (Math.abs(clamped - rawProgressRef.current) > FAST_SCROLL_DELTA) {
          fastScrollRef.current = true;
          if (fastScrollTimeoutRef.current != null) window.clearTimeout(fastScrollTimeoutRef.current);
          fastScrollTimeoutRef.current = window.setTimeout(() => {
            fastScrollRef.current = false;
            fastScrollTimeoutRef.current = null;
          }, 260);
        }
        // This only records where the scroll input says we should be. A separate easing loop
        // (below) is what actually moves progressRef -- the value applyTransforms reads -- toward
        // this target, so a burst of scroll speed doesn't translate 1:1 into a burst of movement.
        // snapWarp additionally holds that target at the nearest work for a stretch of scroll
        // input before releasing it -- see its own comment above.
        rawProgressRef.current = snapWarp(clamped);
        setProgressPct(Math.max(0, Math.min(1, y / total)));
      });
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", computeUnit);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", computeUnit);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      if (fastScrollTimeoutRef.current != null) window.clearTimeout(fastScrollTimeoutRef.current);
      if (holdTimeoutRef.current != null) window.clearTimeout(holdTimeoutRef.current);
    };
  }, [slides.length, reducedMotion, scrollUnitFactor]);

  // Eases progressRef toward rawProgressRef every frame instead of snapping to it, so movement
  // feels driven rather than dumped on screen. The catch-up rate isn't uniform: right around a
  // room boundary it slows down further, so the visitor reads as decelerating into the threshold
  // rather than being carried through at whatever speed the scroll input happened to be moving.
  // This only changes how quickly progress catches up to the scroll input -- none of the
  // recede/darkness/emerge visual sequencing in applyTransforms changes; it still just reads
  // progressRef exactly as before.
  useEffect(() => {
    if (reducedMotion) return;

    function isNearRoom(p: number) {
      const idx = Math.round(p);
      return slides[idx]?.kind === "room" || slides[idx - 1]?.kind === "room" || slides[idx + 1]?.kind === "room";
    }

    function tick() {
      const target = rawProgressRef.current;
      const current = progressRef.current;
      const diff = target - current;
      if (Math.abs(diff) > 0.0008) {
        // While a forced hold (see onScroll above) is in effect, converge quickly and
        // decisively to the held index instead of the slow near-room damp -- the point of the
        // hold is a confident, fully-visible beat, not a slow crawl into view.
        const damp = holdIndexRef.current != null ? 0.12 : isNearRoom(current) ? 0.04 : 0.22;
        progressRef.current = current + diff * damp;
      } else if (current !== target) {
        progressRef.current = target;
      }
      const nearest = Math.round(progressRef.current);
      setCenterIndex((prev) => (prev === nearest ? prev : nearest));
      applyTransforms();
      smoothRafRef.current = window.requestAnimationFrame(tick);
    }

    smoothRafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (smoothRafRef.current != null) window.cancelAnimationFrame(smoothRafRef.current);
    };
  }, [slides.length, reducedMotion]);

  useEffect(() => {
    applyTransforms();
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        handleExit();
        return;
      }
      if (reducedMotion) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        scrollToIndex(Math.min(slides.length - 1, Math.round(progressRef.current) + 1));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        scrollToIndex(Math.max(0, Math.round(progressRef.current) - 1));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, reducedMotion]);

  function scrollToIndex(idx: number) {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const top = wrapper.getBoundingClientRect().top + window.scrollY;
    const target = offsetsRef.current[idx] ?? idx * unitRef.current;
    window.scrollTo({ top: top + target, behavior: "smooth" });
  }

  function handleExit() {
    if (reducedMotion) {
      exit();
      return;
    }
    // A hard cut straight from the corridor to the timeline read as abrupt. The corridor's
    // background (#0a0a0a) and the timeline's (GROUND, #0b0a09) are already almost the same
    // near-black, so fading in the same veil jumpToIndex uses below, then swapping while it's
    // fully opaque, reads as one continuous darkening rather than a jarring content swap.
    setJumpVeilVisible(true);
    window.setTimeout(() => {
      exit();
    }, 900);
  }

  function handleNext() {
    if (!onNext) return;
    if (reducedMotion) {
      onNext();
      return;
    }
    setJumpVeilVisible(true);
    window.setTimeout(() => {
      onNext();
    }, 900);
  }

  function handlePrev() {
    if (!onPrev) return;
    if (reducedMotion) {
      onPrev();
      return;
    }
    setJumpVeilVisible(true);
    window.setTimeout(() => {
      onPrev();
    }, 900);
  }

  function jumpToIndex(idx: number) {
    setShowIndex(false);
    if (reducedMotion) {
      scrollToIndex(idx);
      return;
    }
    setJumpVeilVisible(true);
    window.setTimeout(() => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const top = wrapper.getBoundingClientRect().top + window.scrollY;
        const target = offsetsRef.current[idx] ?? idx * unitRef.current;
        window.scrollTo({ top: top + target, behavior: "auto" });
      }
      rawProgressRef.current = idx;
      progressRef.current = idx;
      const nearest = Math.round(idx);
      setCenterIndex((prev) => (prev === nearest ? prev : nearest));
      applyTransforms();
      window.setTimeout(() => setJumpVeilVisible(false), 300);
    }, 900);
  }

  if (slides.length === 0) {
    return (
      <div style={{ padding: "96px 44px", color: "#111" }}>
        <p>The collection is still loading. One moment.</p>
      </div>
    );
  }

  const visibleIndices: number[] = [];
  for (let i = Math.max(0, centerIndex - WINDOW_RADIUS); i <= Math.min(slides.length - 1, centerIndex + WINDOW_RADIUS); i++) {
    visibleIndices.push(i);
  }

  return (
    <EventNaturalsContext.Provider value={eventNaturals}>
    <EventProbe items={eventItems} isMobile={isMobile} onMeasure={setEventNaturals} />
    <div style={{ fontFamily: "Inter, Arial, sans-serif" }}>
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 20,
          background: "#0a0a0a",
          opacity: jumpVeilVisible ? 1 : 0,
          pointerEvents: jumpVeilVisible ? "auto" : "none",
          transition: "opacity 900ms ease",
        }}
      />

      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "2px", zIndex: 10, background: "rgba(255,255,255,0.08)" }}>
        <div
          style={{
            height: "100%",
            width: `${progressPct * 100}%`,
            background: "#f2f2f2",
            transition: "width 120ms linear",
          }}
        />
      </div>

      <div
        onClick={() => handleExit()}
        style={{
          position: "fixed",
          top: isMobile ? "16px" : "24px",
          left: isMobile ? "20px" : "40px",
          zIndex: 10,
          fontFamily: "Cormorant Garamond, serif",
          fontSize: isMobile ? "13px" : "14px",
          letterSpacing: "0.02em",
          color: "#f2f2f2",
          cursor: "pointer",
          opacity: chromeVisible ? 1 : 0,
          transition: "opacity 900ms ease",
          textShadow: "0 1px 3px rgba(0,0,0,0.9), 0 1px 10px rgba(0,0,0,0.6)",
        }}
      >
        MoDDA
      </div>

      <div
        style={{
          position: "fixed",
          bottom: isMobile ? "16px" : "24px",
          left: isMobile ? "20px" : "40px",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {multiRoom && (
          <button onClick={() => setShowIndex((s) => !s)} style={navButtonStyle}>
            Index
          </button>
        )}
        {onPrev && prevLabel && (
          <button onClick={() => handlePrev()} style={navButtonStyle}>
            ← Back: {prevLabel}
          </button>
        )}
        {onPrev && prevLabel && onNext && nextLabel && (
          // Invisible Exit-sized spacer -- keeps this stack the same height as the Next/Exit
          // stack on the other corner, so Back lines up with Next instead of with Exit.
          <button aria-hidden style={{ ...navButtonStyle, visibility: "hidden", pointerEvents: "none" }}>
            Exit
          </button>
        )}
      </div>

      <div
        style={{
          position: "fixed",
          bottom: isMobile ? "16px" : "24px",
          right: isMobile ? "20px" : "40px",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "10px",
        }}
      >
        {onNext && nextLabel && (
          // Sian: pulse the Next link once the visitor reaches the end of a room, so the way
          // forward is obvious. Only on the last slide; static highlight under reduced motion (CSS).
          <button
            onClick={() => handleNext()}
            style={navButtonStyle}
            className={atEnd || centerIndex >= slides.length - 1 ? "mw-next-pulse" : undefined}
          >
            Next: {nextLabel} →
          </button>
        )}
        <button onClick={() => handleExit()} style={navButtonStyle}>
          Exit
        </button>
      </div>

      {reducedMotion ? (
        // Needs position + zIndex like the scroll-driven wrapper below (see the comment on
        // ProofOfCultureLine's `backdrop`): Proof of Culture always renders a permanent
        // full-bleed backdrop behind the corridor at zIndex 0, and this content is otherwise
        // an ordinary in-flow (non-positioned) block. A non-positioned element can never paint
        // above a positioned one regardless of DOM order or z-index -- so on any device with
        // "reduce motion" turned on at the OS level, this entire branch was rendering directly
        // underneath that backdrop: correct in the DOM, invisible on screen, every time,
        // regardless of browser. (This is what Sian's screen recording actually showed -- not
        // an animation timing issue.) position: relative + zIndex: 1 fixes that without
        // changing layout or scroll behavior at all.
        <div style={{ position: "relative", zIndex: 1, background: "#0a0a0a", color: "#f2f2f2", padding: "120px 24px 100px" }}>
          {slides.map((slide, i) => {
            // Room-title slides are a few lines of text, not an image -- forcing them into a
            // fixed 75vh box left "THE BEGINNING" centered in a mostly-empty near-screen-height
            // block, an oversized gap before and after it. Event cards have the same problem:
            // EventCardBlock sizes itself in JS off its own measured content (a ResizeObserver
            // against its real rendered height), not a CSS percentage, so it doesn't need a
            // tall fixed box either -- it was just inheriting one from being mapped the same way
            // as a work. Only work/image slides actually need the fixed height: WalkthroughMedia
            // sizes its frame as a PERCENTAGE of this box (MEDIA_MAX_HEIGHT_PCT) so a tall image
            // can't render unconstrained -- see that constant's comment. So room titles AND
            // event cards both get height: auto (only as tall as their real content), and every
            // slide type shares one smaller margin -- Sian: "spaces between event cards are too
            // big" / "space between artworks are still too big, make the spacing consistent."
            // This whole branch was invisible on screen until the reduced-motion stacking fix
            // above, so nothing in its layout had ever actually been seen/tuned on a real device.
            const isRoomTitle = slide.kind === "room";
            const isEventSlide = slide.kind === "item" && isEvent(slide.item);
            const isAutoHeight = isRoomTitle || isEventSlide;
            // Mobile gets its own (smaller) numbers here: on a 375x812 phone 65vh work slides
            // and a 48px margin between EVERY slide (Sian, mobile: "space between artworks
            // are still too big", "spaces between event cards are too big") left far more dead
            // black space per scroll-step than the same values do on a wide desktop viewport.
            const slideMarginBottom = isMobile ? "28px" : "48px";
            const workHeight = isMobile ? "48vh" : "65vh";
            return (
              <div
                key={i}
                style={{
                  marginBottom: slideMarginBottom,
                  textAlign: "center",
                  height: isAutoHeight ? "auto" : workHeight,
                  padding: isAutoHeight ? (isMobile ? "12px 0" : "24px 0") : undefined,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SlideContent slide={slide} isMobile={isMobile} allowVideo fitToViewport={false} />
              </div>
            );
          })}
        </div>
      ) : (
        <div ref={wrapperRef} style={{ position: "relative", height: `${totalHeight}px` }}>
          <div
            className={softEntry ? "mw-soft" : undefined}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: "#0a0a0a",
              overflow: "hidden",
              opacity: softEntry ? (softVisible ? 1 : 0) : 1,
              transition: softEntry ? "opacity 900ms ease" : undefined,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: isMobile ? "64px" : "88px",
                bottom: isMobile ? "72px" : "96px",
                left: 0,
                right: 0,
              }}
            >
              {visibleIndices.map((i) => {
                const slide = slides[i];
                const allowVideo = Math.abs(i - centerIndex) <= VIDEO_PLAY_RADIUS;
                // The room title fades in on its own slower rhythm (see .mw-item-room-title
                // below). The first work of a new room gets its own delayed fade too, but that
                // now lives on its media/caption elements via the isArrival prop passed to
                // SlideContent below, not on this wrapper -- see .mw-media-arrival /
                // .mw-caption-arrival in the CSS.
                const itemClassName = slide.kind === "room" ? "mw-item mw-item-room-title" : "mw-item";
                return (
                  <div
                    key={i}
                    className={itemClassName}
                    ref={(el) => {
                      if (el) itemRefs.current.set(i, el);
                      else itemRefs.current.delete(i);
                    }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      willChange: "transform, opacity",
                    }}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        maxWidth: "90vw",
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SlideContent
                        slide={slide}
                        isMobile={isMobile}
                        allowVideo={allowVideo}
                        isArrival={slides[i - 1]?.kind === "room"}
                        startHidden
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {multiRoom && showIndex && (
        <div
          onClick={() => setShowIndex(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,10,10,0.96)",
            zIndex: 30,
            overflowY: "auto",
            padding: isMobile ? "80px 24px" : "100px 60px",
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "640px", margin: "0 auto" }}>
            <h3
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontSize: "28px",
                fontWeight: 400,
                marginBottom: "28px",
                fontStyle: "italic",
                color: "#f2f2f2",
              }}
            >
              Jump to a room
            </h3>
            {rooms
              .filter((r) => r.items.length > 0)
              .map((room) => {
                const slideIdx = slides.findIndex((s) => s.room.title === room.title);
                return (
                  <div
                    key={room.title}
                    onClick={() => jumpToIndex(slideIdx)}
                    style={{
                      padding: "16px 0",
                      borderBottom: "1px solid #292929",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#f2f2f2",
                    }}
                  >
                    <span style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "19px" }}>
                      {room.title}
                    </span>
                    <span style={{ fontFamily: MONO_FONT, color: "#777", fontSize: "12px" }}>{room.countLabel ?? worksLabel(roomWorkCount(room))}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
    </EventNaturalsContext.Provider>
  );
}
